import { Component, Element, Fragment, getAssetPath, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import {
  CarouselImage,
  CarouselImageList,
  CCParams,
  ConfigItem,
  Exterior,
  ModelDetail,
  ModelGroupDetail,
  Variant,
  Wheel,
} from './ConfigData';
import {
  BRAND,
  BREAKPOINT,
  BreakpointConfig,
  CtaTarget,
  MccConfigError,
  MccConfigurationStep,
  MccError,
  MccLoadError,
  THEME,
} from './typings';
import { getCountryCodeFromLocale, getThemeForBrand } from '../../utils/utils';
import { TranslationService } from '../../services/translation-service';

@Component({
  tag: 'phs-marketing-cc',
  styleUrl: 'marketing-cc.scss',
  shadow: true,
  assetsDirs: ['assets/AUDI23', 'assets/CUPRA24', 'assets/SEAT24', 'assets/SKODA23', 'assets/VW22', 'assets/default'],
})
export class MarketingCC {

  private translationService = TranslationService;
  private DATA_TYPE_WHEEL = "Rim";
  private DATA_TYPE_EXTERIOR = "Color";
  private DATA_TYPE_VARIANT = "Model Variant";

  @Element() host: HTMLPhsMarketingCcElement;

  /**
   * Holds the current locale used for CC calls.
   */
  @Prop() locale: string;

  /**
   * Holds the 1 character brand code of the brand of the vehicle.
   */
  @Prop({mutable: true}) brand: BRAND;

  /**
   * Holds the 8 character model code of the vehicle. Only this specific model will be configurable (cannot be set in conjunction with "modelgroupcode").
   */
  @Prop({attribute: 'modelcode'}) modelCodeParam: string;

  /**
   * Holds a 3 character modelGroup code. The user can select a variant within the given modelGroup to configure (cannot be set in conjunction with "modelcode").
   */
  @Prop({attribute: 'modelgroupcode'}) modelGroupCodeParam: string;

  /**
   * (optional)  Exteriors that will be excluded from the selection.
   */
  @Prop({attribute: 'exclude-exterior'}) exteriorExclude: string;

  /**
   * (optional)  The only exteriors that should be included in the selection.
   */
  @Prop({attribute: 'include-exterior'}) exteriorInclude: string;

  /**
   * (optional)  Wheels that will be excluded from the selection.
   */
  @Prop({attribute: 'exclude-wheel'}) wheelExclude: string;

  /**
   * (optional)  The only wheels that should be included in the selection.
   */
  @Prop({attribute: 'include-wheel'}) wheelInclude: string;

  /**
   * (optional)  Variants that will be excluded from the selection.
   */
  @Prop({attribute: 'exclude-variant'}) variantExclude: string;

  /**
   * (optional)  The only variants that should be included in the selection.
   */
  @Prop({attribute: 'include-variant'}) variantInclude: string;

  /**
   * (optional)  Specifies whether interior images should be displayed in the image slider as well.
   */
  @Prop({attribute: 'display-interior'}) displayInterior: boolean = false;

  /**
   * (optional)  An optional override field for the text in the CTA button.
   */
  @Prop({attribute: 'cta-text'}) ctaText: string;

  /**
   * (optional)  The CC target page of the CTA Button
   */
  @Prop({attribute: 'cta-target'}) ctaTarget: CtaTarget = CtaTarget.DESIGN;

  /**
   * (optional)  Default selection for the color of the vehicle.
   */
  @Prop({
    reflect: true,
    mutable: true
  }) color: string = "@";

  /**
   * (optional)  Default selection for the interior of the vehicle.
   */
  @Prop({
    reflect: true,
    mutable: true
  }) interior: string = "@";

  /**
   * (optional)  Default selection for the wheels of the vehicle.
   */
  @Prop({
    reflect: true,
    mutable: true
  }) equipments: string = "@";

  /**
   * (optional)  indicates if the qa urls should be used instead of the prod urls.
   */
  @Prop() qa: boolean = false;

  @State() configurationStep: MccConfigurationStep = MccConfigurationStep.EXTERIOR;
  @State() selectedVariant: Variant;
  @State() variantsDropdownOpen: boolean = false;
  @State() activeBreakpoints: BREAKPOINT[];
  @State() isExteriorActive: boolean = true;
  @State() isWheelActive: boolean = false;
  @State() showPrevIcon: boolean = false;
  @State() showNextIcon: boolean = true;
  @State() navChange = false;
  private loadDataBeforeRender: boolean = true;
  private ccParams: CCParams = {};

  private rotationImageList: CarouselImageList[];
  private carouselImageList: CarouselImageList[];
  private error: MccError = null;
  private carouselImages: CarouselImage[] = [];
  private rotationImages: CarouselImage[] = [];
  private dataLayer = window.dataLayer || [];
  private availableExteriors: Exterior[];
  private availableWheels: Wheel[];
  private availableVariants: Variant[];
  private filteredVariants: Variant[];

  private selectedExterior: Exterior;
  private selectedWheel: Wheel;
  private currentIndex = 6;
  private currentTranslate = 0;
  private currentSlideWidth = 68;
  private currentTiles = 6;

  private subBrand: string;
  private theme: THEME;
  private modelGroupCode: string;
  private modelCode: string;
  private resizeObserver: ResizeObserver;
  private imageSlider: HTMLMccImageSliderElement;
  private rotationView: HTMLMccRotationViewElement;
  private hostWidth: number;
  private maxWheelWidth: string = '';
  private maxExteriorWidth: string = '';
  private fallbackWidth: string = '';
  private maxWidth: number = 48;
  private lastBreakpoints: BREAKPOINT[] = [];

  componentWillLoad() {
    document.addEventListener('click', () => this.variantsDropdownOpen = false);
    this.hostWidth = window.innerWidth;
  }

  componentWillUpdate() {
    this.error = null;
  }

  componentWillRender() {
    if (!this.locale || (!this.modelCodeParam && !this.modelGroupCodeParam) || !this.brand) {
      const msg = `locale, brand and modelcode (or modelgroupcode) must be set: ${this.locale}, ${this.brand}, ${this.modelCodeParam}, ${this.modelGroupCodeParam}`;
      this.error = new MccConfigError(msg);
      throw this.error;
    }
    if ((this.exteriorInclude && this.exteriorExclude)
      || (this.wheelExclude && this.wheelInclude)
      || (this.variantExclude && this.variantInclude)) {
      const msg = 'includes and excludes of one type cannot be set simultaneously.';
      this.error = new MccConfigError(msg);
      throw this.error;
    }
    if (this.brand as string === 'R') {
      this.subBrand = 'SC';
      this.brand = BRAND.SEAT;
    }
    this.theme = getThemeForBrand(this.brand, this.subBrand);
    const translationsCall = this.translationService.load(this.locale, this.theme, this.qa);
    this.loadFonts();
    if (this.loadDataBeforeRender) {
      this.loadDataBeforeRender = false;
      const dataCall = this.loadData().catch(error => {
        console.error('Some error occurred during data loading...', error, error.url);
        this.notifyAdminZone('Data', error.url);
      });
      return Promise.all([dataCall, translationsCall]);
    }
    return translationsCall;
  }

  componentDidRender() {
    const resizeElement = this.host.shadowRoot.querySelector('#nwi-root');
    this.resizeObserver = new ResizeObserver(event => {
      const elementWidth = event ? event[0]?.contentRect.width : 0;
      this.hostWidth = elementWidth;
      this.setBreakpointClasses(elementWidth, event[0]?.target);
      this.setBreakpointClasses(elementWidth, this.imageSlider?.shadowRoot.querySelector('#nwi-root'));
      this.setBreakpointClasses(elementWidth, this.rotationView?.shadowRoot.querySelector('#nwi-root'));

      this.setTiles(elementWidth);

      if (!!this.rotationImages && !!this.rotationImageList) {
        const images = [];
        for (let responsiveImages of this.rotationImageList) {
          images.push(...this.computeImageList(responsiveImages));
        }
        this.rotationImages = images;
      }

      if (!!this.carouselImages && !!this.carouselImageList) {
        const images = [];
        for (let responsiveImage of this.carouselImageList) {
          if (this.displayInterior || responsiveImage.type === 'EXTERIOR') {
            images.push(...this.computeImageList(responsiveImage));
          }
        }
        this.carouselImages = images;
      }
    });
    this.resizeObserver.observe(resizeElement);
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }

  @Watch('modelCodeParam')
  @Watch('modelGroupCodeParam')
  @Watch('selectedVariant')
  @Watch('color')
  @Watch('equipments')
  @Watch('exteriorExclude')
  @Watch('exteriorInclude')
  @Watch('wheelExclude')
  @Watch('wheelInclude')
  @Watch('variantExclude')
  @Watch('variantInclude')
  @Watch('qa')
  @Watch('displayInterior')
  watchPropertyChange() {
    this.loadDataBeforeRender = true;
  }

  @Watch('modelGroupCodeParam')
  watchModelGroupCodeHandler() {
    this.availableVariants = null;
    this.filteredVariants = null;
    this.selectedVariant = null;
  }

  @Listen('mccImageLoadError')
  handleMccImageLoadError() {
    this.notifyAdminZone('Image');
  }

  notifyAdminZone(type: 'Image' | 'Data', request?: string) {
    const source = (() => {
      const url = new URL(window.location.href);
      return encodeURIComponent(`${url.origin}${url.pathname}`);
    })();
    request = request ? encodeURIComponent(request) : request;

    const ccAdminUrl = `https://${this.qa ? 'qa-' : ''}cc-admin.porscheinformatik.com/cc-admin-at/open/api/admin/mccError/${this.brand}?country=${getCountryCodeFromLocale(this.locale)}&type=${type}&source=${source}&request=${request}`;
    fetch(ccAdminUrl).then(response => {
      if (!response.ok) console.error('AdminZone call failed.', response.status, response.statusText);
    }).catch(reason => console.error(reason));
  }

  loadFonts() {
    const theme = this.theme === THEME.LNF22 ? THEME.VW22 : this.theme;
    const fontsCssUrl = getAssetPath(`./assets/${theme}/fonts.css`);

    // check if link tag already exists
    if (document.querySelectorAll('head link[href="' + fontsCssUrl + '"]').length > 0) {
      return;
    }

    // load fonts for theme
    const stylesheet = document.createElement('link');
    stylesheet.setAttribute('rel', 'stylesheet');
    stylesheet.setAttribute('href', fontsCssUrl);
    stylesheet.setAttribute('type', 'text/css');
    stylesheet.onload = () => console.debug('Fonts loaded!');
    document.head.appendChild(stylesheet);
  }

  async loadData(): Promise<void> {
    console.debug('Loading data from CC...');

    if (this.modelGroupCodeParam && !this.modelCodeParam) {
      if (!this.availableVariants) {
        const modelGroupDetail: ModelGroupDetail = await fetch(this.buildModelGroupDetailUrl()).then((response) => {
          this.checkForError(response);
          return response.json();
        });
        this.availableVariants = modelGroupDetail.variants;
      }
      this.filteredVariants = this.filterVariants(this.availableVariants, this.variantExclude, this.variantInclude);
      if (!this.selectedVariant || !this.filteredVariants.some(v => this.selectedVariant.code === v.code)) {
        this.selectedVariant = this.filteredVariants[0];
      }
      this.modelCode = this.selectedVariant.cheapestModelCode;
    }
    if (this.modelCodeParam) {
      this.modelCode = this.modelCodeParam;
    }

    let modelDetail: ModelDetail = await this.getModelDetail();

    this.setDataFromModelDetail(modelDetail);

    // reset selectedItems to default if the currently selected are not available
    let reloadData = false;
    if (!this.availableExteriors.some(ext => ext.code === this.selectedExterior?.code)) {
      this.selectedExterior = this.availableExteriors[0];
      this.color = this.selectedExterior.code;
      reloadData = true;
    }

    if (!this.availableWheels.some(wheel => wheel.code === this.selectedWheel?.code)) {
      this.selectedWheel = this.availableWheels[0];
      this.setWheelInEquipments(this.selectedWheel.code);
      reloadData = true;
    }
    if (reloadData) {
      modelDetail = await this.getModelDetail();
      this.setDataFromModelDetail(modelDetail);
    }

    this.subBrand = modelDetail.subBrand;
    if (this.modelGroupCode !== modelDetail.modelGroupCode) {
      this.modelGroupCode = modelDetail.modelGroupCode;
      this.dataLayer.push({
        event: 'marketingcc-view',
        carModelGroup: modelDetail.modelGroupName,
        carModelGroupId: this.modelGroupCode,
      });
    }

    this.maxExteriorWidth = this.maxWidth * this.availableExteriors.length + (5 * this.maxExteriorWidth.length) + 8 + 'px'; // 5 gap 4 padding
    this.maxWheelWidth = this.maxWidth * this.availableWheels.length + (5 * this.maxWheelWidth.length) + 8 + 'px'; // 5 gap 4 padding
  }

  private setDataFromModelDetail(modelDetail: ModelDetail) {
    // set images
    this.carouselImages = this.getImages(modelDetail);
    this.rotationImages = this.getRotationImages(modelDetail);

    // filter exteriors and wheels according to rules
    this.availableExteriors = this.filterCodes(modelDetail.availableExteriors, this.exteriorExclude, this.exteriorInclude) as Exterior[];
    this.availableWheels = this.filterCodes(modelDetail.availableWheels, this.wheelExclude, this.wheelInclude) as Wheel[];

    // initially set the values to the selected properties from the modelDetail call
    this.selectedExterior = modelDetail.selectedExterior;
    this.selectedWheel = modelDetail.selectedWheel;

    this.equipments = modelDetail.equipmentCode;
    this.color = modelDetail.exterior;
    this.interior = modelDetail.interior;
  }

  private getModelDetail(modelOnly: boolean = false): Promise<ModelDetail> {
    return fetch(this.buildModelDetailUrl(modelOnly)).then(response => {
      this.checkForError(response);
      return response.json();
    });
  }

  private checkForError(response: Response) {
    if (!response.ok) {
      const msg = `Call to '${response.url}' failed with status code: ${response.status}.`;
      this.error = new MccLoadError(msg, response.status, response.url);
      throw this.error;
    }
    if (response.headers.get('content-length') === '0') {
      const msg = 'Got an empty response.';
      this.error = new MccLoadError(msg, response.status, response.url);
      throw this.error;
    }
  }

  private getImages(modelDetail: ModelDetail): CarouselImage[] {
    const images = new Array<CarouselImage>();
    for (let carouselImages of modelDetail.stageImages.carouselImages) {
      this.carouselImageList = modelDetail.stageImages.carouselImages;
      if (this.displayInterior || carouselImages.type === 'EXTERIOR') {
        if (THEME.AUDI23) {
          images.forEach(img => {
            if (!img.url.includes('&background=full')) {
              img.url = img.url + '&background=full';
            }
          });
        }
        images.push(...this.computeImageList(carouselImages));
      }
    }
    return images;
  }

  private computeImageList(carouselImages: CarouselImageList) {
    const images = new Array<CarouselImage>();
    const list = carouselImages.imageList;
    list.sort((a, b) => a.width - b.width);
    const calculatedImage = list.find(image => image.width >= this.hostWidth)
      || list[list.length - 1];
    if (THEME.AUDI23) {
      calculatedImage.url = calculatedImage.url + '&background=full';
    }
    images.push(calculatedImage);
    return images;
  }

  private getRotationImages(modelDetail: ModelDetail): CarouselImage[] {
    const images = new Array<CarouselImage>();
    if (modelDetail.stageImages.responsiveRotationImages) {
      this.rotationImageList = modelDetail.stageImages.responsiveRotationImages;
      for (let responsiveImages of modelDetail.stageImages.responsiveRotationImages) {
        if (THEME.VW22 || THEME.LNF22) {
          images.forEach(img => img.url = img.url + '&background=full');
        }
        images.push(...this.computeImageList(responsiveImages));
      }
    }
    return images;
  }

  private buildModelGroupDetailUrl() {
    return `https://${this.qa ? 'qa-' : ''}cc.porscheinformatik.com/cc-${getCountryCodeFromLocale(this.locale).toLowerCase()}/be`
      + `/${this.locale}_${getThemeForBrand(this.brand, this.subBrand)}/api/v2/modelgroupDetail`
      + `?code=${this.modelGroupCodeParam}&brand=${this.brand}&mcc=true`;
  }

  private buildModelDetailUrl(modelOnly: boolean) {
    const base = `https://${this.qa ? 'qa-' : ''}cc.porscheinformatik.com/cc-${getCountryCodeFromLocale(this.locale).toLowerCase()}/be`
      + `/${this.locale}_${getThemeForBrand(this.brand, this.subBrand)}/api/v2/modeldetail/`
      + `${this.brand}/${this.modelCode}`;
    if (modelOnly) {
      return base + '/@/@/@/@?mcc=true';
    } else {
      const params = [];
      Object.keys(this.ccParams).forEach(key => params.push(key + '=' + this.ccParams[key]));
      return base + `/${this.color || '@'}/${this.interior || '@'}/${this.equipments || '@'}/@?mcc=true&${params.join('&')}`;
    }
  }

  getExteriorName(exterior: Exterior) {
    if (!exterior) {
      return '';
    }
    return exterior.bezf + (exterior.bezv ? ' ' + exterior.bezv : '');
  }

  filterCodes(availableCodes: ConfigItem[], excludes: string, includes: string) {
    if (excludes) {
      return availableCodes.filter(item => !excludes.split(',').map(code => code.trim()).includes(item.code));
    }
    if (includes) {
      return availableCodes.filter(item => includes.split(',').map(code => code.trim()).includes(item.code));
    }
    return availableCodes;
  }

  filterVariants(availableVariants: Variant[], excludes: string, includes: string) {
    if (excludes) {
      const excl = excludes.split(',').map(code => code.trim().toLowerCase());
      return availableVariants.filter(item => !excl.includes(item.code.toLowerCase()) && !excl.includes(item.name.toLowerCase()));
    }
    if (includes) {
      const incl = includes.split(',').map(code => code.trim().toLowerCase());
      return availableVariants.filter(item => incl.includes(item.code.toLowerCase()) || incl.includes(item.name.toLowerCase()));
    }
    return availableVariants;
  }

  removeWheelFromEquipments() {
    const currentWheel = this.extractWheelFromEquipments();
    if (currentWheel) {
      this.equipments = MarketingCC.removeString(this.equipments.split('@'), currentWheel.code).join('@');
    }
  }

  /**
   * Removes a certain string from an array of strings.
   * @param array  Array that will be altered.
   * @param str  String that should be removed from the array.
   */
  private static removeString(array: string[], str: string) {
    if (!array || array.length === 0) {
      return;
    }
    const index = array.indexOf(str);
    array.splice(index, 1);
    return array;
  }

  setWheelInEquipments(wheelCode: string) {
    this.removeWheelFromEquipments();
    this.equipments = this.equipments && this.equipments !== '@' ? this.equipments + `@${wheelCode}` : wheelCode;
  }

  extractWheelFromEquipments(): Wheel {
    const filteredWheels = this.availableWheels.filter(wheel => this.equipments.split('@').some(equipmentCode => wheel.code === equipmentCode));
    if (filteredWheels.length === 0) {
      return null;
    }
    if (filteredWheels.length !== 1) {
      throw Error('More than one Wheel in equipment codes!');
    }
    return filteredWheels[0];
  }

  selectVariant(variantCode: string) {
    this.selectedVariant = this.filteredVariants.find(variant => variant.code === variantCode);
    this.variantsDropdownOpen = false;
    this.pushSelectEvent(this.DATA_TYPE_VARIANT, this.selectedVariant.name);
  }

  selectedContainer(nav: string) {
    this.showPrevIcon = false;
    this.showNextIcon = true;

    let slider: HTMLDivElement;
    if (nav === 'WHEEL') {
      const selectedExteriorCode = this.selectedExterior.code;
      const exteriorIndex = this.availableExteriors.findIndex(exterior => exterior.code === selectedExteriorCode);

      if (exteriorIndex > -1) {
        const [selectedExterior] = this.availableExteriors.splice(exteriorIndex, 1);
        this.availableExteriors.unshift(selectedExterior);
      }
      slider = this.host.shadowRoot.querySelector('.exterior-selection');
      this.isExteriorActive = false;
      this.isWheelActive = true;
    }

    if (nav === 'EXTERIOR') {
      const wheelIndex = this.availableWheels.findIndex(wheel => wheel.code === this.selectedWheel.code);

      if (wheelIndex > -1) {
        const [selectedWheel] = this.availableWheels.splice(wheelIndex, 1);
        this.availableWheels.unshift(selectedWheel);
      }
      slider = this.host.shadowRoot.querySelector('.wheel-selection');
      this.isExteriorActive = true;
      this.isWheelActive = false;
    }
    this.currentTranslate = 0;
    slider.style.transition = `transform 0.0s ease-in-out`;
    this.setSliderPosition(slider);
  }

  selectExterior(color: string) {
    this.ccParams = {
      F0: this.color,
      F: color
    };
    this.color = color;
    this.selectedExterior = this.availableExteriors.find(ext => ext.code === this.color);
    this.pushSelectEvent(this.DATA_TYPE_EXTERIOR, this.selectedExterior.bezf);
  }

  selectWheel(wheel: string) {
    this.ccParams = {
      A: wheel
    };
    this.setWheelInEquipments(wheel);
    this.selectedWheel = this.availableWheels.find(ext => ext.code === wheel);
    this.pushSelectEvent(this.DATA_TYPE_WHEEL, this.selectedWheel.title);
  }

  filterWheels() {
    if (this.isExteriorActive && this.selectedWheel) {
      return [this.selectedWheel];
    }
    return this.availableWheels;
  }

  filterExterior() {
    if (this.isWheelActive && this.selectedExterior) {
      return [this.selectedExterior];
    }
    return this.availableExteriors;
  }

  prevSlide(slider: HTMLDivElement) {
    this.currentIndex = this.currentIndex - this.currentTiles + 2;
    this.showNextIcon = true;
    if (this.currentIndex < this.currentTiles) {
      this.currentIndex = this.currentTiles;
      this.showPrevIcon = false;
      this.showNextIcon = true;
    }
    this.setPositionByIndex(slider);
  }

  nextSlide(available: number, slider: HTMLDivElement) {
    let addTranslate = 0;
    this.showPrevIcon = true;
    this.currentIndex = this.currentIndex + this.currentTiles - 2; // exclude prev and next button
    if (this.currentIndex > available) {
      this.currentIndex = available;
      this.showPrevIcon = true;
      this.showNextIcon = false;
      addTranslate = 5; // position of the next button
    }
    this.setPositionByIndex(slider, addTranslate);
  }

  setPositionByIndex(slider: HTMLDivElement, addTranslate: number = 0) {
    this.currentSlideWidth = this.maxWidth + 5; // gap
    this.currentTranslate = (this.currentIndex - this.currentTiles) * -this.currentSlideWidth;
    this.currentTranslate = this.currentTranslate - addTranslate;
    slider.style.transition = `transform 0.6s ease-in-out`;
    this.setSliderPosition(slider);
  }

  setSliderPosition(slider: HTMLDivElement) {
    slider.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  pushSelectEvent(inputType: string, inputValue: string) {
    this.dataLayer.push({
      event: 'marketingcc-select',
      inputType: inputType,
      inputValue: inputValue
    });
  }

  openCC() {
    window.open(this.buildCCUrl());
    this.dataLayer.push({
      event: 'marketingcc-config-continue'
    });
  }

  buildCCUrl() {
    console.log('building cc-url')
    let url = `https://${this.qa ? 'qa-' : ''}cc.porscheinformatik.com/cc-${getCountryCodeFromLocale(this.locale).toLowerCase()}/${this.locale}_${this.theme}/${this.brand}`;
    let extraPart = '';
    switch (this.ctaTarget) {
      case CtaTarget.MODEL_SELECTION:
        if (this.brand === BRAND.SEAT && this.theme !== THEME.CUPRA24) {
          url += `/auv`;
          extraPart = '/drive-select';
        } else {
          url += `/model-selection`;
        }
        break;
      case CtaTarget.EQUIPMENT:
        url += `/equipment`;
        break;
      case CtaTarget.DETAIL:
        url += `/detail`;
        break;
      default:
        url += `/design`;
        extraPart = '/exterior';
        break;
    }
    url += `/${this.modelGroupCode}/${this.modelCode}/${this.color || '@'}/${this.interior || '@'}/${this.equipments || '@'}/@${extraPart}?funnelSource=MarketingCC`;
    if (this.selectedVariant) {
      url += `&variant=${this.selectedVariant.code}`;
    }
    console.log('MCC opens: ', url);
    return url;
  }

  render() {
    const filteredWheels = this.filterWheels();
    const filteredExterior = this.filterExterior();

    if (this.availableWheels.length <= this.currentTiles && this.isWheelActive) {
      this.showNextIcon = false;
    }

    if (this.availableExteriors.length <= this.currentTiles && this.isExteriorActive) {
      this.showNextIcon = false;
    }
    return (
      <Host>
        <div class={this.theme} id="nwi-root">
          {
            this.error ?
              <div class="error">
              </div> :
              <>
                {(this.modelGroupCodeParam && !this.modelCodeParam) && <>
                  {<div class="variants-dropdown-wrapper">
                    <div class="variants-dropdown" onClick={(e) => e.stopPropagation()}>
                      <div class="dropdown-head" onClick={() => this.variantsDropdownOpen = !this.variantsDropdownOpen}>
                        <span class="dropdown-text">{this.selectedVariant?.name}</span>
                        {this.filteredVariants?.length > 1 && this.getIconForCurrentTheme('chevron', 'chevron', this.translationService.get('mcc.chevron.text'))}
                      </div>
                      <div class={'options-container' + (this.variantsDropdownOpen ? ' open' : '')}>
                        {this.filteredVariants?.filter((variant) => variant.code !== this.selectedVariant.code).map((variant) => {
                          return <div
                            class={'option' + (this.selectedVariant?.code === variant.code ? ' selected' : '')}
                            key={variant.code}
                            onClick={() => this.selectVariant(variant.code)}>{variant.name}</div>;
                        })}
                      </div>
                    </div>
                  </div>}
                </>}
                {[THEME.VW22, THEME.LNF22].includes(this.theme) && this.rotationImages ?
                  <mcc-rotation-view ref={(el) => this.rotationView = el} theme={this.theme}
                                     rotationImages={this.rotationImages} /> : null}
                {this.carouselImages && ![THEME.VW22, THEME.LNF22].includes(this.theme) ?
                  <mcc-image-slider ref={(el) => this.imageSlider = el} theme={this.theme}
                                    carouselImages={this.carouselImages} /> : null}
                <div class="content">
                  <div onClick={() => {
                    if (!this.isExteriorActive && this.availableExteriors.length > 1) {
                      this.selectedContainer('EXTERIOR');
                    }
                  }}
                       class={'selection-container' + (this.isExteriorActive ? ' selected' : '')}
                       style={{ width: this.isExteriorActive && this.availableExteriors.length < this.currentTiles ? this.maxExteriorWidth : this.fallbackWidth && this.isExteriorActive ? this.fallbackWidth : undefined }}
                       key={'exterior-selection-container'}>
                    <img src={getAssetPath(`./assets/default/edit-l.svg`)} class={'edit-icon' + (!this.isExteriorActive && this.availableExteriors.length > 1 ? '' : ' hidden')} alt={this.translationService.get('mcc.edit.text')}/>
                    <div class="exterior-selection-wrapper">
                      <div class={'nav-prev' + (this.showPrevIcon && this.isExteriorActive ? ' open' : '')}
                           onClick={() => this.prevSlide(this.host.shadowRoot.querySelector('.exterior-selection'))}>{this.getIconForCurrentTheme('button-backward', 'prev-icon', this.translationService.get('mcc.prev-icon.text'))}</div>
                      <div class="exterior-selection">
                        {filteredExterior.map((ext) =>
                          <div onClick={() => {
                            if (this.isExteriorActive) {
                              this.selectExterior(ext.code);
                            }
                          }
                          } class={{
                            'exterior-container': true,
                            selected: ext.code === this.selectedExterior?.code,
                          }} key={ext.code}>
                            <img class="exterior" src={ext.image} onError={this.useFallbackImage}
                                 alt={this.getExteriorName(ext)}
                                 title={this.getExteriorName(ext)} />
                            {(ext.code === this.selectedExterior?.code && [THEME.VW22, THEME.LNF22].includes(this.theme))
                              && this.getIconForCurrentTheme('select-check', 'checkmark', this.translationService.get('mcc.selected.text'))}
                          </div>,
                        )}
                      </div>
                      <div class={'nav-next' + (this.showNextIcon && this.isExteriorActive ? ' open' : '')}
                           onClick={() => this.nextSlide(this.availableExteriors.length, this.host.shadowRoot.querySelector('.exterior-selection'))}>{this.getIconForCurrentTheme('button-forward', 'next-icon', this.translationService.get('mcc.next-icon.text'))}</div>
                    </div>
                  </div>

                  <div onClick={() => {
                    if (!this.isWheelActive && this.availableWheels.length > 1) {
                      this.selectedContainer('WHEEL');
                    }
                  }} class={'selection-container' + (this.isWheelActive ? ' selected' : '')}
                       style={{ width: this.isWheelActive && this.availableWheels.length < this.currentTiles ? this.maxWheelWidth : this.fallbackWidth && this.isWheelActive ? this.fallbackWidth : undefined }}
                       key={'wheel-selection-container'}>
                    <img src={getAssetPath(`./assets/default/edit-l.svg`)} class={'edit-icon' + (!this.isWheelActive && this.availableWheels.length > 1 ? '' : ' hidden')} alt={this.translationService.get('mcc.edit.text')}/>
                    <div class="wheel-selection-wrapper">
                      <div class={'nav-prev' + (this.showPrevIcon && this.isWheelActive ? ' open' : '')}
                           onClick={() => this.prevSlide(this.host.shadowRoot.querySelector('.wheel-selection'))}>{this.getIconForCurrentTheme('button-backward', 'prev-icon', this.translationService.get('mcc.prev-icon.text'))}</div>
                      <div class="wheel-selection">
                        {filteredWheels.map((wheel) =>
                          <div onClick={() => {
                            if (this.isWheelActive) {
                              this.selectWheel(wheel.code);
                            }
                          }
                          }
                               class={{
                                 'wheel-container': true,
                                 selected: wheel.code === this.selectedWheel?.code,
                               }}
                               key={wheel.code}>
                            <img class="wheel" src={wheel.image} onError={this.useFallbackImage} alt={wheel.title}
                                 title={wheel.title} />
                            {(wheel.code === this.selectedWheel?.code && [THEME.VW22, THEME.LNF22].includes(this.theme))
                              && this.getIconForCurrentTheme('select-check', 'checkmark', this.translationService.get('mcc.selected.text'))}
                          </div>,
                        )}
                      </div>
                      <div class={'nav-next' + (this.showNextIcon && this.isWheelActive ? ' open' : '')}
                           onClick={() => this.nextSlide(this.availableWheels.length, this.host.shadowRoot.querySelector('.wheel-selection'))}>{this.getIconForCurrentTheme('button-forward', 'next-icon', this.translationService.get('mcc.next-icon.text'))}</div>
                    </div>
                  </div>
                </div>
                <div class="cta-section">
                  <div class="cta-wrapper">
                    <button onClick={() => this.openCC()}>
                      <span>{this.ctaText || this.translationService.get('mcc.footer.cta.cc-link')}</span>
                      {[BRAND.AUDI].includes(this.brand) &&
                        <div
                          class="link-icon-wrapper">{this.getIconForCurrentTheme('icon-link', 'link-icon', this.translationService.get('mcc.footer.cta.cc-link'))}</div>}
                    </button>
                  </div>
                </div>
              </>
          }
        </div>
      </Host>
    );
  }

  useFallbackImage(e: Event) {
    const image = e.target as HTMLImageElement;
    image.onerror = null;
    image.src = getAssetPath(`./assets/default/image-not-found.svg`);
  }

  getIconForCurrentTheme(iconName: string, className: string, alt: string) {
    const theme = this.theme === THEME.LNF22 ? THEME.VW22 : this.theme;
    return <img src={getAssetPath(`./assets/${theme}/${iconName}.svg`)} class={`${className} ${this.variantsDropdownOpen ? 'open' : ''}`} alt={alt}/>;
  }

  setBreakpointClasses(elementWidth: number, element: Element) {
    if (element) {
      const activeBreakpoints = [];
      for (const key of Object.keys(BREAKPOINT).reverse()) {
        const breakpointSize = BreakpointConfig.breakpointValue(BREAKPOINT[key]);
        if (breakpointSize <= elementWidth) {
          activeBreakpoints.push(key);
        }

        element.classList.toggle(key, breakpointSize <= elementWidth);
      }

      if (this.activeBreakpoints?.toString() !== activeBreakpoints?.toString()) {
        this.activeBreakpoints = activeBreakpoints;
      }
    }
  }

  setTiles(elementWidth: number) {
    if (this.activeBreakpoints === this.lastBreakpoints) {
      return;
    }

    if (this.activeBreakpoints?.length <= 1) {
      this.currentTiles = 3;
    }

    if (this.activeBreakpoints?.length <= 3) {
      this.currentTiles = Math.floor((elementWidth - 49 - this.maxWidth) / this.maxWidth);
      if (this.currentTiles > 5) {
        this.currentTiles = 5;
      }
      this.fallbackWidth = this.maxWidth * this.currentTiles + (5 * this.currentTiles) + 8 + 'px';
    } else {
      this.currentTiles = 6;
    }

    if (this.activeBreakpoints?.length === 4) {
      this.maxWidth = 58;
    }

    if (this.activeBreakpoints?.length >= 5) {
      this.maxWidth = 68;
    }

    this.currentIndex = this.currentTiles;
    this.lastBreakpoints = this.activeBreakpoints;
  }
}
